/**
 * NmbrsExportPreview — Reviewable preview van een NMBRS export.
 *
 * Toont per medewerker de voorgestelde potje-mutatie en uitbetaalde uren
 * volgens calculateUrensaldoState. Gebruiker kan per rij de mutatie
 * bijsturen. "Bevestigen & exporteren" schrijft een payroll_run,
 * schrijft alle mutaties weg, archiveert de CSV in Supabase Storage en
 * triggert de browser-download.
 */

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../stores/workforce-store';
import type { Staff, TimeEntry, LeaveEntry } from '../../stores/workforce-store';
import {
  calculateSurcharges,
  calculateVacationAccrual,
  calculateMinMaxExtraVacation,
  calculateScheduledHours,
  countRoosterWerkdagen,
  formatHours,
  type PayPeriod,
  type StaffPeriodSummary,
} from '../../lib/hours-calculator';
import {
  calculateUrensaldoState,
  calculatePotjeSaldo,
  type Mutation,
} from '../../lib/urensaldo-calculator';
import {
  generateNmbrsCsv,
  csvToBase64,
  generateNmbrsExcelBlob,
  downloadExcel,
  type NmbrsExportData,
} from '../../lib/nmbrs-export';
import { filterSickLeave, filterNonSickLeave, filterVacationUsed } from '../../lib/leave-types';

interface Props {
  open: boolean;
  onClose: () => void;
  period: PayPeriod;
  activeStaff: Staff[];
  timeEntries: TimeEntry[];
  leaveEntries: LeaveEntry[];
  yearSaldoMap: Map<string, number>;
  onExported?: () => void;
}

// Jaaruren: spaarsaldo-context voor de uitbetalingsregel. In de run over de
// jaargrens (20 dec-19 jan) is dat de jaarafrekening van het vorige referte-jaar
// (plusuren uitbetalen, minuren vervallen t.l.v. werkgever); in alle andere runs
// de cap-overloop (contract-art 4.4: uren boven de cap bij de eerstvolgende
// loonbetaling uitbetalen). Mutatie-uren zijn NEGATIEF: ze verlaten het saldo.
interface JaarurenInfo {
  saldo: number;
  cap: number;
  source: 'jaaruren_uitbetaling' | 'jaaruren_afrekening';
  referteJaar: number;
}

interface RowState {
  staff: Staff;
  workedHours: number;
  leaveHours: number;
  sickHours: number;
  potjeBefore: number;
  suggestedMutation: number; // default signed (deposit+, withdrawal-)
  currentMutation: number;   // user-editable
  paidHoursAuto: number;     // baseHours + currentMutation
  endOfContractAlert: boolean;
  jaaruren: JaarurenInfo | null;
}

function toHHmm(val: string | null | undefined): string | null {
  if (!val) return null;
  if (val.includes('T')) {
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return val.slice(0, 5);
}

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}

export function NmbrsExportPreview({
  open,
  onClose,
  period,
  activeStaff,
  timeEntries,
  leaveEntries,
  yearSaldoMap,
  onExported,
}: Props) {
  const [mutationsByStaff, setMutationsByStaff] = useState<Map<string, Mutation[]>>(new Map());
  const [jaarurenByStaff, setJaarurenByStaff] = useState<Map<string, JaarurenInfo>>(new Map());
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockWarning, setLockWarning] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const exportableStaff = useMemo(
    () => activeStaff.filter(s => s.contract_type !== 'zzp' && s.contract_type !== 'dga'),
    [activeStaff]
  );

  // Fetch all non-voided potje-mutaties voor exportable staff
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setLockWarning(null);
    setActiveRunId(null);
    (async () => {
      try {
        // Check if active run exists for this period
        const runs: Array<{ id: string; status: string; period_label: string }> =
          await apiFetch(`/api/workforce/payroll-runs?period=${encodeURIComponent(period.label)}`);
        const active = runs.find(r => r.status === 'active');
        if (active) {
          setActiveRunId(active.id);
          setLockWarning(
            `Er is al een actieve payroll-run voor ${period.label}. Heropen deze eerst voordat je opnieuw kunt exporteren.`
          );
        }

        // Fetch mutations for each staff
        const map = new Map<string, Mutation[]>();
        await Promise.all(
          exportableStaff.map(async s => {
            const muts: Mutation[] = await apiFetch(
              `/api/workforce/urensaldo-mutations?staff_id=${s.id}`
            );
            map.set(s.id, muts);
          })
        );
        setMutationsByStaff(map);

        // Jaaruren-stand per jaaruren-medewerker: peildatum = periode-einde (19e).
        // Run over de jaargrens (from-jaar < to-jaar) → jaarafrekening vorig jaar op 31-12.
        const fromYear = parseInt(period.from.slice(0, 4), 10);
        const toYear = parseInt(period.to.slice(0, 4), 10);
        const isJaargrensRun = fromYear < toYear;
        const jMap = new Map<string, JaarurenInfo>();
        await Promise.all(
          exportableStaff.filter(s => s.contract_type === 'jaaruren').map(async s => {
            const [year, peildatum, source] = isJaargrensRun
              ? [fromYear, `${fromYear}-12-31`, 'jaaruren_afrekening' as const]
              : [toYear, period.to, 'jaaruren_uitbetaling' as const];
            const stand: { saldo: number; cap: number } = await apiFetch(
              `/api/workforce/jaaruren-stand?staff_id=${s.id}&year=${year}&peildatum=${peildatum}`
            );
            jMap.set(s.id, { saldo: stand.saldo, cap: stand.cap, source, referteJaar: year });
          })
        );
        setJaarurenByStaff(jMap);
      } catch (e) {
        setError((e as Error).message || 'Laden van mutaties mislukt.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, period.label, exportableStaff, reloadKey]);

  // Build rows whenever dependencies change
  useEffect(() => {
    if (!open) return;
    const built: RowState[] = exportableStaff.map(staff => {
      const se = timeEntries.filter(te => te.staff_id === staff.id);
      const sl = leaveEntries.filter(le => le.staff_id === staff.id);
      const workedHours = se.reduce((sum, te) => sum + (te.total_hours || 0), 0);
      const leaveHours = filterNonSickLeave(sl).reduce((s, l) => s + l.hours, 0);
      const sickHours = filterSickLeave(sl).reduce((s, l) => s + l.hours, 0);

      const potjeBefore = calculatePotjeSaldo(mutationsByStaff.get(staff.id) || []);
      const drempel = staff.maand_norm || 173;
      const contractUrenPerMaand = (staff.contract_type === 'vast' || staff.contract_type === 'jaaruren' || staff.contract_type === 'vast-salaris') && staff.uren_per_week
        ? Math.round((staff.uren_per_week * 52 / 12) * 100) / 100
        : null;
      // Min-max: uren_per_week bevat het contractueel minimum aantal uren
      const minUrenPerMaand = staff.contract_type === 'min-max' && staff.uren_per_week
        ? Math.round((staff.uren_per_week * 52 / 12) * 100) / 100
        : null;

      const state = calculateUrensaldoState({
        contractType: staff.contract_type,
        contractUrenPerMaand,
        minUrenPerMaand,
        workedHours,
        leaveHours,
        sickHours,
        drempel,
        potjeBefore,
      });

      let suggested = state.suggestedDeposit - state.suggestedWithdrawal; // signed
      let paidHoursAuto = state.paidHours;

      // Jaaruren: voorstel spaaruren-uitbetaling als NEGATIEVE mutatie
      // (zelfde tekenconventie als potje-withdrawal: paid = base − mutatie).
      const jInfo = staff.contract_type === 'jaaruren' ? jaarurenByStaff.get(staff.id) ?? null : null;
      if (jInfo) {
        const uitTeBetalen = jInfo.source === 'jaaruren_afrekening'
          ? Math.max(0, jInfo.saldo)                                  // jaarafrekening: alle plusuren
          : Math.max(0, Math.round((jInfo.saldo - jInfo.cap) * 100) / 100); // cap-overloop
        suggested = -uitTeBetalen;
        paidHoursAuto = Math.round((state.paidHours + uitTeBetalen) * 100) / 100;
      }

      // Uit-dienst alert: end_date binnen [period.from, period.to + 30 dagen]
      let endOfContractAlert = false;
      if (staff.end_date) {
        const extendedTo = new Date(period.to);
        extendedTo.setDate(extendedTo.getDate() + 30);
        const endStr = extendedTo.toISOString().slice(0, 10);
        if (staff.end_date >= period.from && staff.end_date <= endStr) {
          endOfContractAlert = potjeBefore + suggested > 0;
        }
      }

      // Uitbetalen-regel:
      //  - vast: contracturen gegarandeerd; meerwerk uitbetaald tot drempel, rest naar potje.
      //  - jaaruren: paid = contracturen + spaaruren-uitbetaling (cap-overloop/jaarafrekening)
      //  - overig: paid = baseHours - mutation
      //    * deposit (+): gewerkt boven drempel → paid = drempel
      //    * withdrawal (-): gewerkt onder drempel + potje aanvullen → paid = gewerkt + |withdrawal|
      return {
        staff,
        workedHours,
        leaveHours,
        sickHours,
        potjeBefore,
        suggestedMutation: suggested,
        currentMutation: suggested,
        paidHoursAuto,
        endOfContractAlert,
        jaaruren: jInfo,
      };
    });
    setRows(built);
  }, [open, exportableStaff, timeEntries, leaveEntries, mutationsByStaff, jaarurenByStaff, period.from, period.to]);

  function updateMutation(staffId: string, newValue: number) {
    setRows(prev => prev.map(r => {
      if (r.staff.id !== staffId) return r;
      if (r.staff.contract_type === 'vast-salaris') {
        // Vast-salaris: geen urenregistratie, meerwerk in het salaris; altijd de contracturen, nooit een mutatie.
        const contractUren = r.staff.uren_per_week
          ? Math.round((r.staff.uren_per_week * 52 / 12) * 100) / 100
          : 0;
        return { ...r, currentMutation: 0, paidHoursAuto: contractUren };
      }
      if (r.staff.contract_type === 'jaaruren') {
        // Jaaruren: mutatie = spaaruren-uitbetaling, altijd <= 0 (nooit storten).
        // paid = contracturen + |mutatie|.
        const v = Math.min(0, newValue);
        const contractUren = r.staff.uren_per_week
          ? Math.round((r.staff.uren_per_week * 52 / 12) * 100) / 100
          : 0;
        return { ...r, currentMutation: v, paidHoursAuto: Math.round((contractUren - v) * 100) / 100 };
      }
      if (r.staff.contract_type === 'vast') {
        // Vast: contracturen gegarandeerd; meerwerk uitbetaald tot drempel.
        // Mutatie-aanpassing schuift tussen uitbetalen en potje (nooit onder contract).
        const contractFloor = r.staff.uren_per_week
          ? Math.round((r.staff.uren_per_week * 52 / 12) * 100) / 100
          : 0;
        const vastBase = Math.max(r.workedHours + r.leaveHours + r.sickHours, contractFloor);
        const vastPaid = Math.round(Math.max(contractFloor, vastBase - newValue) * 100) / 100;
        return { ...r, currentMutation: newValue, paidHoursAuto: vastPaid };
      }
      // baseHours - mutatie, met min-uren floor voor min-max:
      //   deposit (+) → paid wordt lager (het verschil gaat naar potje)
      //   withdrawal (-) → paid wordt hoger (potje vult aan)
      const rawBase = r.workedHours + r.leaveHours + r.sickHours;
      const minFloor = r.staff.contract_type === 'min-max' && r.staff.uren_per_week
        ? Math.round((r.staff.uren_per_week * 52 / 12) * 100) / 100
        : 0;
      const baseHours = Math.max(rawBase, minFloor);
      const paid = Math.round((baseHours - newValue) * 100) / 100;
      return { ...r, currentMutation: newValue, paidHoursAuto: paid };
    }));
  }

  function buildExportData(): NmbrsExportData[] {
    return rows.map(row => {
      const { staff, workedHours, leaveHours, sickHours, potjeBefore, currentMutation, paidHoursAuto } = row;
      const se = timeEntries.filter(te => te.staff_id === staff.id);
      const sl = leaveEntries.filter(le => le.staff_id === staff.id);
      // Surcharges
      const surcharges = { normalHours: 0, surchargeHours: 0, surchargeAmount: 0, breakdown: [] as { slot: string; hours: number; percentage: number; amount: number }[] };
      for (const te of se) {
        if (te.clock_in && te.clock_out && staff.uurloon) {
          const ci = toHHmm(te.clock_in);
          const co = toHHmm(te.clock_out);
          if (ci && co) {
            const sc = calculateSurcharges(ci, co, te.date, staff.uurloon, te.break_minutes ?? 0, te.paid_break_minutes ?? 0);
            surcharges.surchargeAmount += sc.surchargeAmount;
            for (const b of sc.breakdown) {
              const ex = surcharges.breakdown.find(x => x.slot === b.slot);
              if (ex) { ex.hours += b.hours; ex.amount += b.amount; }
              else surcharges.breakdown.push({ ...b });
            }
          }
        }
      }
      // Vakantie-opbouw: vast/min-max hebben een vast jaartegoed op contract-/minimumuren;
      // hier alleen de 10% extra opbouw over meerwerk boven de eigen maandnorm.
      // 0-uren/student: 10% over alle gewerkte uren (geen jaartegoed).
      // 10% bij all-in blijft hier bewust staan: dit is de uitbetaling in geld voor de
      // accountant, geen verlofopbouw (zie contract-regime.ts vakantieInUurloon; de
      // saldo-lezers geven bij all-in 0 opbouw).
      const vacationAccrual = staff.contract_type === 'vast-salaris'
        // Vast-salaris: vakantie in dagen per contract (jaartegoed), geen periodieke opbouw
        ? calculateVacationAccrual(0)
        : staff.contract_type === 'jaaruren'
        // Jaaruren: jaartegoed op contracturen; opbouw alleen over de in deze run
        // uitbetaalde spaaruren (cap-overloop/jaarafrekening), conform contract-art 4.4.
        ? calculateVacationAccrual(Math.max(0, -currentMutation))
        : (staff.contract_type === 'min-max' || staff.contract_type === 'vast')
        ? calculateMinMaxExtraVacation(workedHours, staff.uren_per_week || 0)
        : calculateVacationAccrual(workedHours);
      const vacationTaken = filterVacationUsed(sl).reduce((s, l) => s + l.hours, 0);
      const hasWerkrooster = (staff.contract_type === 'vast' || staff.contract_type === 'jaaruren' || staff.contract_type === 'vast-salaris') && staff.werkrooster && staff.werkrooster.length > 0;
      const sched = hasWerkrooster
        ? calculateScheduledHours(
            staff.werkrooster!, staff.team, period.from, period.to, se,
            sl.map(l => ({ date: l.date, hours: l.hours, leave_type: l.leave_type })),
            staff.datum_in_dienst || staff.start_date || null,
          )
        : null;
      // Vast-salaris registreert geen uren: werkdagen uit het rooster (min feestdagen en verlof), anders uit de klok
      const staffWerkdagen = staff.contract_type === 'vast-salaris' && sched
        ? countRoosterWerkdagen(sched)
        : new Set(se.filter(te => te.total_hours && te.total_hours > 0).map(te => te.date)).size;
      const reiskostenPerDag = staff.reiskostenvergoeding_per_dag || 0;

      const summary: StaffPeriodSummary = {
        staffId: staff.id,
        period,
        scheduledHours: sched?.totalScheduled || 0,
        workedHours,
        paidHours: paidHoursAuto,
        overtimeSurplus: 0,
        surcharges,
        vacationAccrual,
        leaveHours,
        sickHours,
      };

      return {
        staffId: staff.id,
        name: staff.name,
        contractType: staff.contract_type,
        loonType: staff.loon_type,
        sfMedewerkerId: staff.sf_medewerker_id,
        uurloon: staff.uurloon || 0,
        maandsalaris: staff.contract_type === 'vast-salaris' ? (staff.maandsalaris ?? null) : null,
        summary,
        vacationTaken,
        werkdagen: staffWerkdagen,
        reiskostenvergoeding: staffWerkdagen * reiskostenPerDag,
        yearSaldo: yearSaldoMap.get(staff.id) ?? null,
        potjeBefore,
        potjeMutation: currentMutation,
        potjeAfter: Math.round((potjeBefore + currentMutation) * 100) / 100,
      };
    });
  }

  async function handleConfirm() {
    setExporting(true);
    setError(null);
    try {
      const exportData = buildExportData();
      const csv = generateNmbrsCsv(exportData, period);
      const csvBase64 = csvToBase64(csv);

      const mutations = rows
        .filter(r => r.currentMutation !== 0)
        .map(r => {
          if (r.jaaruren) {
            // Jaaruren: spaaruren-uitbetaling (negatief), gebonden aan het referte-jaar
            // zodat jaaruren-stand de uitbetaling van het juiste jaarsaldo aftrekt.
            return {
              staffId: r.staff.id,
              hours: r.currentMutation,
              source: r.jaaruren.source,
              referteJaar: r.jaaruren.referteJaar,
              reason: r.jaaruren.source === 'jaaruren_afrekening'
                ? `Jaarafrekening ${r.jaaruren.referteJaar}: ${formatHours(-r.currentMutation)} plusuren uitbetaald (saldo ${formatHours(r.jaaruren.saldo)})`
                : `Spaaruren boven cap ${formatHours(r.jaaruren.cap)} uitbetaald (saldo ${formatHours(r.jaaruren.saldo)})`,
            };
          }
          return {
            staffId: r.staff.id,
            hours: r.currentMutation,
            source: r.endOfContractAlert && r.currentMutation < 0 ? 'end_of_contract' : 'nmbrs_export',
            reason: r.endOfContractAlert
              ? `Uitbetaling potje bij uitdiensttreding per ${r.staff.end_date}`
              : r.currentMutation > 0
                ? `Overuren gestort (gewerkt ${formatHours(r.workedHours)})`
                : `Topup tot ${(r.staff.maand_norm || 173)}u (gewerkt ${formatHours(r.workedHours)})`,
          };
        });

      await apiFetch('/api/workforce/payroll-runs', {
        method: 'POST',
        body: JSON.stringify({
          periodLabel: period.label,
          periodFrom: period.from,
          periodTo: period.to,
          mutations,
          csvBase64,
        }),
      });

      const xlsxBlob = generateNmbrsExcelBlob(exportData, period);
      downloadExcel(xlsxBlob, `nmbrs-export-${period.label.replace(' ', '-')}.xlsx`);
      onExported?.();
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Export mislukt.');
    } finally {
      setExporting(false);
    }
  }

  async function handleReopen() {
    if (!activeRunId) return;
    const reason = window.prompt(
      `Weet je zeker dat je de payroll-run voor ${period.label} wilt heropenen?\n\n` +
      `Alle eerder weggeschreven mutaties worden gevoid.\n\n` +
      `Geef een korte reden op (min 5 tekens):`,
    );
    if (!reason) return;
    if (reason.trim().length < 5) {
      setError('Reden moet minimaal 5 tekens zijn.');
      return;
    }
    setReopening(true);
    setError(null);
    try {
      await apiFetch(`/api/workforce/payroll-runs?action=reopen&id=${activeRunId}`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      // Force re-fetch: clears warning + reloads mutations zodat potje-saldi kloppen
      setReloadKey(k => k + 1);
    } catch (e) {
      setError((e as Error).message || 'Heropenen mislukt.');
    } finally {
      setReopening(false);
    }
  }

  if (!open) return null;

  const endOfContractStaff = rows.filter(r => r.endOfContractAlert);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="w-full max-w-6xl rounded-xl bg-white p-6 shadow-xl my-8">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold text-main">NMBRS Export Preview</h2>
            <p className="text-sm text-muted capitalize">
              Periode {period.label} — {period.from} t/m {period.to}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
          >
            Sluiten
          </button>
        </div>

        {lockWarning && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <span className="flex-1">⚠️ {lockWarning}</span>
            {activeRunId && (
              <button
                type="button"
                onClick={handleReopen}
                disabled={reopening}
                className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {reopening ? 'Heropenen...' : 'Heropen run'}
              </button>
            )}
          </div>
        )}

        {endOfContractStaff.length > 0 && (
          <div className="mb-4 rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900">
            <p className="font-semibold">Uit dienst deze/volgende periode:</p>
            <ul className="mt-1 list-disc pl-5">
              {endOfContractStaff.map(r => (
                <li key={r.staff.id}>
                  <span className="font-medium">{r.staff.name}</span>: potje staat op{' '}
                  <span className="font-semibold">{formatHours(r.potjeBefore)}</span> (end_date{' '}
                  {r.staff.end_date}). Overweeg volledige uitbetaling.
                </li>
              ))}
            </ul>
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-sm text-muted">Mutaties laden...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-muted">
                  <th className="px-3 py-2">Medewerker</th>
                  <th className="px-3 py-2 text-right">Gewerkt</th>
                  <th className="px-3 py-2 text-right">Verlof</th>
                  <th className="px-3 py-2 text-right">Ziekte</th>
                  <th className="px-3 py-2 text-right">Potje voor</th>
                  <th className="px-3 py-2 text-right">Mutatie</th>
                  <th className="px-3 py-2 text-right">Uitbetalen</th>
                  <th className="px-3 py-2 text-right">Potje na</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => {
                  const isVast = r.staff.contract_type === 'vast';
                  const potjeAfter = Math.round((r.potjeBefore + r.currentMutation) * 100) / 100;
                  const diffFromSuggested = Math.abs(r.currentMutation - r.suggestedMutation) > 0.005;
                  return (
                    <tr key={r.staff.id} className={r.endOfContractAlert ? 'bg-orange-50/50' : ''}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-main">{r.staff.name}</p>
                        <p className="text-[10px] capitalize text-muted">
                          {r.staff.contract_type} · norm {r.staff.maand_norm || 173}u
                          {r.staff.contract_type === 'vast-salaris' && (
                            r.staff.maandsalaris
                              ? <> · € {r.staff.maandsalaris.toLocaleString('nl-NL', { minimumFractionDigits: 2 })} per maand</>
                              : <span className="text-orange-600"> · maandsalaris ontbreekt op de kaart (draai CAO uurlonen)</span>
                          )}
                          {r.jaaruren && (
                            <> · saldo {formatHours(r.jaaruren.saldo)} / cap {formatHours(r.jaaruren.cap)}
                              {r.jaaruren.source === 'jaaruren_afrekening' && ` · jaarafrekening ${r.jaaruren.referteJaar}`}
                            </>
                          )}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-right">{formatHours(r.workedHours)}</td>
                      <td className="px-3 py-2 text-right">{r.leaveHours ? formatHours(r.leaveHours) : '-'}</td>
                      <td className="px-3 py-2 text-right">{r.sickHours ? formatHours(r.sickHours) : '-'}</td>
                      <td className="px-3 py-2 text-right">{formatHours(r.potjeBefore)}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          step="0.25"
                          value={r.currentMutation}
                          onChange={e => updateMutation(r.staff.id, Number(e.target.value))}
                          disabled={r.staff.contract_type === 'vast-salaris'}
                          title={r.staff.contract_type === 'vast-salaris' ? 'Vast salaris: altijd de contracturen, geen potje-mutatie' : undefined}
                          className={`w-20 rounded border px-2 py-1 text-right font-mono text-xs ${
                            diffFromSuggested
                              ? 'border-indigo-400 bg-indigo-50'
                              : 'border-gray-200'
                          }`}
                        />
                        {diffFromSuggested && (
                          <span className="ml-1 text-[10px] text-indigo-600" title={`Voorstel: ${r.suggestedMutation.toFixed(2)}`}>*</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {isVast ? formatHours(r.paidHoursAuto) : formatHours(r.paidHoursAuto)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={potjeAfter < 0 ? 'text-red-600' : 'text-main'}>
                          {formatHours(potjeAfter)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <p className="text-xs text-muted">
            {rows.length} medewerker{rows.length === 1 ? '' : 's'} · {rows.filter(r => r.currentMutation !== 0).length} mutatie{rows.filter(r => r.currentMutation !== 0).length === 1 ? '' : 's'}
            {' '}· <span className="text-indigo-600">* = aangepast t.o.v. voorstel</span>
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={exporting}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={exporting || loading || !!lockWarning}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {exporting ? 'Exporteren...' : 'Bevestigen & exporteren (Excel)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export helper for UrenPage to compute paidHours consistent with preview
export function daysUntilEndOfContract(staff: Staff, period: PayPeriod): number | null {
  if (!staff.end_date) return null;
  return daysBetween(period.from, staff.end_date);
}
